import React, { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { MagnifyingGlassIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import api from '../../services/api';
import { Modal } from '../../components/ui/Modal';
import { Pagination } from '../../components/ui/Pagination';
import {
  badgeClass,
  dangerButtonClass,
  Field,
  FormActions,
  formatMoney,
  formatNumber,
  getApiErrorMessage,
  inputClass,
  inputErrorClass,
  primaryButtonClass,
  QUANTITY_STEP,
  secondaryButtonClass,
} from '../../components/ui/FormControls';
import { usePermissions } from '../../hooks/usePermissions';
import { cn } from '../../lib/utils';
import { defaultRowsPerPage } from '../../lib/preferences';

type State = 'draft' | 'in_force' | 'upcoming' | 'expired' | 'closed';

interface ContractRow {
  id: string;
  contractNumber: string;
  customerId: string;
  customerName: string;
  customerReference: string | null;
  validFrom: string;
  validTo: string;
  state: State;
  daysLeft: number;
  items: number;
}
interface ContractPage {
  data: ContractRow[];
  total: number;
  totalPages: number;
}
interface ContractItem {
  id: string;
  productId: string;
  productName: string;
  sku: string;
  unit: string;
  rate: number;
  contractQuantity: number | null;
  listPrice: number;
  orderedQuantity: number;
}
interface Contract extends Omit<ContractRow, 'items'> {
  notes: string | null;
  closedReason: string | null;
  items: ContractItem[];
}
interface Summary {
  inForce: number;
  customers: number;
  endingSoon: number;
  drafts: number;
  expired: number;
}
interface Choice {
  id: string;
  name: string;
}
interface ProductChoice {
  id: string;
  name: string;
  sku: string;
  unit: string;
  price: number;
}
interface FormLine {
  productId: string;
  name: string;
  sku: string;
  unit: string;
  listPrice: number;
  rate: string;
  contractQuantity: string;
}

const PAGE_SIZES = [10, 25, 50, 100];
const thClass = 'px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500';
const STATE: Record<State, { label: string; className: string }> = {
  draft: { label: 'Draft', className: 'bg-gray-50 text-gray-700 ring-gray-500/20' },
  in_force: { label: 'In force', className: 'bg-green-50 text-green-700 ring-green-600/20' },
  upcoming: { label: 'Starts later', className: 'bg-blue-50 text-blue-700 ring-blue-600/20' },
  expired: { label: 'Expired', className: 'bg-amber-50 text-amber-800 ring-amber-600/20' },
  closed: { label: 'Closed', className: 'bg-gray-50 text-gray-500 ring-gray-500/20' },
};

const today = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
};
const formatDate = (value?: string | null) =>
  value ? new Date(`${String(value).slice(0, 10)}T00:00:00`).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
const clean = (params: object) => Object.fromEntries(Object.entries(params).filter(([, value]) => value !== undefined && value !== ''));

const contractApi = {
  page: async (params: object): Promise<ContractPage> => (await api.get('/rate-contracts', { params: clean(params) })).data,
  summary: async (): Promise<Summary> => (await api.get('/rate-contracts/summary')).data,
  one: async (id: string): Promise<Contract> => (await api.get(`/rate-contracts/${id}`)).data,
  create: async (data: object): Promise<Contract> => (await api.post('/rate-contracts', data)).data,
  update: async (id: string, data: object): Promise<Contract> => (await api.put(`/rate-contracts/${id}`, data)).data,
  act: async (id: string, action: string, data: object = {}): Promise<Contract> => (await api.post(`/rate-contracts/${id}/${action}`, data)).data,
  customers: async (): Promise<Choice[]> => {
    const result = (await api.get('/customers', { params: { limit: 100 } })).data;
    return (Array.isArray(result) ? result : result?.data ?? []) as Choice[];
  },
  products: async (search: string): Promise<ProductChoice[]> => (await api.get('/inventory/paginated', { params: { search, limit: 8, page: 1 } })).data.data,
};

// ---------------------------------------------------------------- draft or change a contract

const ContractForm: React.FC<{ contract: Contract | null; isPending: boolean; onCancel: () => void; onSubmit: (data: object) => void }> = ({ contract, isPending, onCancel, onSubmit }) => {
  const { data: customers = [] } = useQuery({ queryKey: ['rateContracts', 'customers'], queryFn: contractApi.customers });
  const [form, setForm] = useState({ customerId: contract?.customerId ?? '', customerReference: contract?.customerReference ?? '', validFrom: contract?.validFrom ?? today(), validTo: contract?.validTo ?? '', notes: contract?.notes ?? '' });
  const [lines, setLines] = useState<FormLine[]>(() =>
    (contract?.items ?? []).map((item) => ({ productId: item.productId, name: item.productName, sku: item.sku, unit: item.unit, listPrice: item.listPrice, rate: String(item.rate), contractQuantity: item.contractQuantity === null ? '' : String(item.contractQuantity) })),
  );
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchInput.trim()), 300);
    return () => clearTimeout(timer);
  }, [searchInput]);
  const { data: found = [], isFetching } = useQuery({ queryKey: ['rateContracts', 'products', search], queryFn: () => contractApi.products(search), enabled: search.length >= 2 });
  const choices = found.filter((product) => !lines.some((line) => line.productId === product.id));

  const set = (field: keyof typeof form) => (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setForm((prev) => ({ ...prev, [field]: event.target.value }));
    setErrors((prev) => {
      const { [field]: _cleared, ...rest } = prev;
      return rest;
    });
  };
  const setLine = (index: number, patch: Partial<FormLine>) => {
    setLines((prev) => prev.map((line, position) => (position === index ? { ...line, ...patch } : line)));
    setErrors((prev) => {
      const { [`line-${index}`]: _cleared, lines: _all, ...rest } = prev;
      return rest;
    });
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    const next: Record<string, string> = {};
    if (!form.customerId) next.customerId = 'Choose the customer';
    if (!form.validFrom) next.validFrom = 'Enter the first day';
    if (!form.validTo) next.validTo = 'Enter the last day';
    else if (form.validFrom && form.validTo < form.validFrom) next.validTo = 'Not before the first day';
    if (lines.length === 0) next.lines = 'Add at least one product';
    lines.forEach((line, index) => {
      const rate = Number(line.rate);
      if (line.rate.trim() === '' || !Number.isFinite(rate) || rate <= 0) next[`line-${index}`] = 'Enter a rate above zero';
      else if (line.contractQuantity.trim() !== '' && (!Number.isFinite(Number(line.contractQuantity)) || Number(line.contractQuantity) < 0)) next[`line-${index}`] = 'Enter a quantity';
    });
    setErrors(next);
    if (Object.keys(next).length > 0) return;
    onSubmit({
      customerId: form.customerId,
      customerReference: form.customerReference.trim() || null,
      validFrom: form.validFrom,
      validTo: form.validTo,
      notes: form.notes.trim() || null,
      items: lines.map((line) => ({ productId: line.productId, rate: Number(line.rate), contractQuantity: line.contractQuantity.trim() === '' ? null : Number(line.contractQuantity) })),
    });
  };

  return (
    <form onSubmit={handleSubmit} noValidate className="space-y-4">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Field label="Customer" htmlFor="rc-customer" required error={errors.customerId}>
          <select id="rc-customer" value={form.customerId} onChange={set('customerId')} className={cn(inputClass, errors.customerId && inputErrorClass)}>
            <option value="">Choose…</option>
            {customers.map((customer) => (<option key={customer.id} value={customer.id}>{customer.name}</option>))}
          </select>
        </Field>
        <Field label="Their PO / contract number" htmlFor="rc-reference">
          <input id="rc-reference" type="text" maxLength={80} value={form.customerReference} onChange={set('customerReference')} className={inputClass} />
        </Field>
        <Field label="Rates apply from" htmlFor="rc-from" required error={errors.validFrom}>
          <input id="rc-from" type="date" value={form.validFrom} onChange={set('validFrom')} className={cn(inputClass, errors.validFrom && inputErrorClass)} />
        </Field>
        <Field label="Until" htmlFor="rc-to" required error={errors.validTo}>
          <input id="rc-to" type="date" min={form.validFrom || undefined} value={form.validTo} onChange={set('validTo')} className={cn(inputClass, errors.validTo && inputErrorClass)} />
        </Field>
        <Field label="Notes" htmlFor="rc-notes" className="sm:col-span-2">
          <input id="rc-notes" type="text" maxLength={500} value={form.notes} onChange={set('notes')} className={inputClass} />
        </Field>
      </div>

      <section className="rounded-lg border border-gray-200">
        <h4 className="rounded-t-lg border-b border-gray-200 bg-gray-50 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-gray-500">Agreed rates · {lines.length}</h4>
        {lines.length > 0 && (
          <ul className="divide-y divide-gray-100">
            {lines.map((line, index) => (
              <li key={line.productId} className="space-y-1 px-3 py-2">
                <div className="flex flex-wrap items-center gap-3">
                  <p className="min-w-0 flex-1 text-sm text-gray-900">
                    {line.name} <span className="text-xs text-gray-500">{line.sku}</span>
                    <span className="block text-xs text-gray-500">List price {formatMoney(line.listPrice)}</span>
                  </p>
                  <input type="number" min="0" step="0.01" value={line.rate} onChange={(event) => setLine(index, { rate: event.target.value })} placeholder="Rate ₹" aria-label={`Rate of ${line.name}`} className={cn(inputClass, 'w-32 py-1 text-right tabular-nums', errors[`line-${index}`] && inputErrorClass)} />
                  <input type="number" min="0" step={QUANTITY_STEP} value={line.contractQuantity} onChange={(event) => setLine(index, { contractQuantity: event.target.value })} placeholder="Agreed qty" aria-label={`Agreed quantity of ${line.name}`} className={cn(inputClass, 'w-32 py-1 text-right tabular-nums')} />
                  <span className="w-10 text-xs text-gray-500">{line.unit}</span>
                  <button type="button" onClick={() => setLines((prev) => prev.filter((_, position) => position !== index))} aria-label={`Remove ${line.name}`} className="rounded-md p-1.5 text-gray-400 hover:bg-red-50 hover:text-red-600">
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </div>
                {errors[`line-${index}`] && <p className="text-sm text-red-600">{errors[`line-${index}`]}</p>}
              </li>
            ))}
          </ul>
        )}
        <div className="border-t border-gray-200 p-3">
          <div className="relative">
            <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input id="rc-search" type="search" value={searchInput} onChange={(event) => setSearchInput(event.target.value)} placeholder="Search product or SKU to add" aria-label="Search products to add" className={cn(inputClass, 'pl-9', errors.lines && inputErrorClass)} />
          </div>
          {errors.lines && <p className="mt-1 text-sm text-red-600">{errors.lines}</p>}
          {search.length >= 2 && (
            <ul className="mt-1 max-h-44 divide-y divide-gray-100 overflow-y-auto rounded-md border border-gray-200">
              {choices.length === 0 ? (
                <li className="px-3 py-2 text-sm text-gray-500">{isFetching ? 'Searching…' : 'No product matches'}</li>
              ) : (
                choices.map((product) => (
                  <li key={product.id}>
                    <button
                      type="button"
                      onClick={() => {
                        setLines((prev) => [...prev, { productId: product.id, name: product.name, sku: product.sku, unit: product.unit, listPrice: Number(product.price) || 0, rate: '', contractQuantity: '' }]);
                        setSearchInput('');
                        setSearch('');
                        setErrors((prev) => {
                          const { lines: _cleared, ...rest } = prev;
                          return rest;
                        });
                      }}
                      className="flex w-full items-center justify-between gap-3 px-3 py-2 text-left text-sm hover:bg-indigo-50"
                    >
                      <span>
                        <span className="font-medium text-gray-900">{product.name}</span> <span className="text-gray-500">{product.sku}</span>
                      </span>
                      <span className="text-xs text-gray-500 tabular-nums">{formatMoney(Number(product.price) || 0)}</span>
                    </button>
                  </li>
                ))
              )}
            </ul>
          )}
        </div>
      </section>

      <FormActions>
        <button type="button" onClick={onCancel} className={secondaryButtonClass}>
          Cancel
        </button>
        <button type="submit" disabled={isPending} className={primaryButtonClass}>
          {isPending ? 'Saving…' : contract ? 'Save Contract' : 'Draft Contract'}
        </button>
      </FormActions>
    </form>
  );
};

// ---------------------------------------------------------------- one contract

const ContractPanel: React.FC<{ contractId: string; canEdit: boolean; canApprove: boolean; onEdit: (contract: Contract) => void; onClose: () => void; onChanged: () => void }> = ({ contractId, canEdit, canApprove, onEdit, onClose, onChanged }) => {
  const queryClient = useQueryClient();
  const { data: contract, isLoading, isError, error } = useQuery({ queryKey: ['rateContracts', 'one', contractId], queryFn: () => contractApi.one(contractId) });
  const [asking, setAsking] = useState<'extend' | 'close' | null>(null);
  const [value, setValue] = useState('');

  const act = useMutation({
    mutationFn: ({ action, data }: { action: string; data?: object }) => contractApi.act(contractId, action, data),
    onSuccess: (saved, { action }) => {
      const message: Record<string, string> = { activate: `${saved.contractNumber} activated`, extend: `${saved.contractNumber} extended to ${formatDate(saved.validTo)}`, close: `${saved.contractNumber} closed` };
      toast.success(message[action]);
      queryClient.setQueryData(['rateContracts', 'one', contractId], saved);
      setAsking(null);
      setValue('');
      onChanged();
    },
    onError: (actionError) => toast.error(getApiErrorMessage(actionError, 'That could not be done'), { duration: 7000 }),
  });

  if (isLoading) return <p className="py-10 text-center text-sm text-gray-500">Loading…</p>;
  if (isError || !contract) return <p className="py-10 text-center text-sm text-gray-700">{getApiErrorMessage(error, 'The contract could not be loaded.')}</p>;
  const active = contract.state === 'in_force' || contract.state === 'upcoming' || contract.state === 'expired';

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-x-6 gap-y-1 text-sm text-gray-700">
        <span className={cn(badgeClass, STATE[contract.state].className)}>{STATE[contract.state].label}</span>
        <span className="font-semibold text-gray-900">{contract.customerName}</span>
        <span>
          {formatDate(contract.validFrom)} to {formatDate(contract.validTo)}
          {contract.state === 'in_force' && <span className={contract.daysLeft <= 30 ? 'font-medium text-amber-700' : 'text-gray-500'}> · {formatNumber(contract.daysLeft)} day(s) left</span>}
        </span>
        {contract.customerReference && <span>Their reference {contract.customerReference}</span>}
        {contract.notes && <span className="text-gray-500">{contract.notes}</span>}
        {contract.closedReason && <span className="text-gray-500">Closed: {contract.closedReason}</span>}
      </div>
      {contract.state === 'in_force' && <p className="text-xs text-gray-500">While this contract is in force, quotes and orders for {contract.customerName} are offered these rates, ahead of any price list.</p>}

      <div className="overflow-x-auto rounded-lg border border-gray-200">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th scope="col" className={thClass}>Product</th>
              <th scope="col" className={cn(thClass, 'text-right')}>Contract rate</th>
              <th scope="col" className={cn(thClass, 'text-right')}>List price</th>
              <th scope="col" className={cn(thClass, 'text-right')}>Agreed quantity</th>
              <th scope="col" className={cn(thClass, 'text-right')}>Ordered in the period</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {contract.items.map((item) => (
              <tr key={item.id}>
                <td className="px-4 py-2 text-sm text-gray-900">
                  {item.productName} <span className="text-xs text-gray-500">{item.sku}</span>
                </td>
                <td className="whitespace-nowrap px-4 py-2 text-right text-sm font-medium text-gray-900 tabular-nums">{formatMoney(item.rate)}</td>
                <td className="whitespace-nowrap px-4 py-2 text-right text-sm text-gray-700 tabular-nums">{formatMoney(item.listPrice)}</td>
                <td className="whitespace-nowrap px-4 py-2 text-right text-sm text-gray-700 tabular-nums">{item.contractQuantity === null ? '—' : `${formatNumber(item.contractQuantity)} ${item.unit}`}</td>
                <td className="whitespace-nowrap px-4 py-2 text-right text-sm text-gray-700 tabular-nums">
                  {formatNumber(item.orderedQuantity)} {item.unit}
                  {item.contractQuantity !== null && item.contractQuantity > 0 && <span className="block text-xs text-gray-500">{formatNumber(Math.round((item.orderedQuantity / item.contractQuantity) * 100))}% of what was agreed</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {asking ? (
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
          {asking === 'extend' ? (
            <Field label="New last day" htmlFor="rp-extend" required>
              <input id="rp-extend" type="date" min={contract.validTo} value={value} onChange={(event) => setValue(event.target.value)} className={cn(inputClass, 'max-w-xs')} />
            </Field>
          ) : (
            <Field label="Why is it closed?" htmlFor="rp-close" required>
              <input id="rp-close" type="text" maxLength={300} value={value} onChange={(event) => setValue(event.target.value)} className={inputClass} />
            </Field>
          )}
          <div className="mt-3 flex justify-end gap-2">
            <button type="button" onClick={() => { setAsking(null); setValue(''); }} className={secondaryButtonClass}>
              Back
            </button>
            <button
              type="button"
              disabled={act.isPending || (asking === 'extend' ? value <= contract.validTo : value.trim().length < 3)}
              onClick={() => act.mutate(asking === 'extend' ? { action: 'extend', data: { validTo: value } } : { action: 'close', data: { reason: value.trim() } })}
              className={asking === 'extend' ? primaryButtonClass : dangerButtonClass}
            >
              {asking === 'extend' ? 'Confirm Extension' : 'Confirm Closing'}
            </button>
          </div>
        </div>
      ) : (
        <div className="flex flex-wrap justify-end gap-2 border-t border-gray-200 pt-3">
          <button type="button" onClick={onClose} className={secondaryButtonClass}>
            Close Window
          </button>
          {contract.state === 'draft' && canEdit && (
            <button type="button" onClick={() => onEdit(contract)} className={secondaryButtonClass}>
              Edit
            </button>
          )}
          {contract.state !== 'closed' && canApprove && (
            <button type="button" onClick={() => setAsking('close')} disabled={act.isPending} className={secondaryButtonClass}>
              Close Contract
            </button>
          )}
          {active && contract.state !== 'expired' && canApprove && (
            <button type="button" onClick={() => { setValue(''); setAsking('extend'); }} disabled={act.isPending} className={secondaryButtonClass}>
              Extend
            </button>
          )}
          {contract.state === 'draft' && canApprove && (
            <button type="button" onClick={() => act.mutate({ action: 'activate' })} disabled={act.isPending} className={primaryButtonClass}>
              Activate
            </button>
          )}
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------- the page

const RateContracts: React.FC = () => {
  const { can } = usePermissions();
  const canCreate = can('sales.create');
  const canEdit = can('sales.update');
  const canApprove = can('sales.approve');
  const queryClient = useQueryClient();

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(defaultRowsPerPage());
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [stateFilter, setStateFilter] = useState<State | ''>('');
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<Contract | null>(null);
  const [viewingId, setViewingId] = useState<string | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const { data, isLoading, isError, error, isFetching } = useQuery({
    queryKey: ['rateContracts', 'page', { page, limit, search, state: stateFilter }],
    queryFn: () => contractApi.page({ page, limit, search, state: stateFilter }),
    placeholderData: keepPreviousData,
  });
  const { data: summary } = useQuery({ queryKey: ['rateContracts', 'summary'], queryFn: contractApi.summary });
  const { data: viewing } = useQuery({ queryKey: ['rateContracts', 'one', viewingId], queryFn: () => contractApi.one(viewingId as string), enabled: !!viewingId });

  const refreshLists = () => ['page', 'summary'].forEach((key) => queryClient.invalidateQueries({ queryKey: ['rateContracts', key] }));
  const closeForm = () => {
    setFormOpen(false);
    setEditing(null);
  };
  const save = useMutation({
    mutationFn: (input: object) => (editing ? contractApi.update(editing.id, input) : contractApi.create(input)),
    onSuccess: (contract) => {
      toast.success(editing ? `${contract.contractNumber} saved` : `${contract.contractNumber} drafted`);
      queryClient.setQueryData(['rateContracts', 'one', contract.id], contract);
      refreshLists();
      closeForm();
      setViewingId(contract.id);
    },
    onError: (submitError) => toast.error(getApiErrorMessage(submitError, 'The contract could not be saved')),
  });

  const rows = data?.data ?? [];
  const total = data?.total ?? 0;
  const totalPages = data?.totalPages ?? 1;
  const hasFilters = !!search || !!stateFilter;
  const firstRow = total > 0 ? (page - 1) * limit + 1 : 0;
  const lastRow = Math.min(page * limit, total);
  const stats = [
    { label: 'In force', value: formatNumber(summary?.inForce ?? 0), hint: `With ${formatNumber(summary?.customers ?? 0)} customer(s)` },
    { label: 'Ending within 30 days', value: formatNumber(summary?.endingSoon ?? 0), hint: 'Renew or extend before they lapse' },
    { label: 'Drafts', value: formatNumber(summary?.drafts ?? 0), hint: 'Not yet activated' },
    { label: 'Expired', value: formatNumber(summary?.expired ?? 0), hint: 'Past their last day' },
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Rate Contracts</h1>
          <p className="mt-1 text-sm text-gray-500">Prices agreed with a customer for a period. While a contract is in force its rates are what quotes and orders for that customer are offered.</p>
        </div>
        {canCreate && (
          <button type="button" onClick={() => setFormOpen(true)} className={primaryButtonClass}>
            <PlusIcon className="-ml-0.5 mr-2 h-4 w-4" />
            New Rate Contract
          </button>
        )}
      </div>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        {stats.map((stat) => (
          <div key={stat.label} className="rounded-lg bg-white p-4 shadow-sm ring-1 ring-gray-200">
            <p className="text-xs font-medium uppercase tracking-wide text-gray-500">{stat.label}</p>
            <p className="mt-1 text-2xl font-semibold text-gray-900">{stat.value}</p>
            <p className="mt-1 text-xs text-gray-500">{stat.hint}</p>
          </div>
        ))}
      </div>

      <div className="overflow-hidden rounded-lg bg-white shadow-sm ring-1 ring-gray-200">
        <div className="flex flex-col gap-3 border-b border-gray-200 p-4 lg:flex-row lg:items-center">
          <div className="relative flex-1">
            <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input type="search" value={searchInput} onChange={(event) => setSearchInput(event.target.value)} placeholder="Search by contract number, customer, their reference or product" aria-label="Search rate contracts" className={cn(inputClass, 'pl-9')} />
          </div>
          <select value={stateFilter} onChange={(event) => { setStateFilter(event.target.value as State | ''); setPage(1); }} aria-label="State" className={cn(inputClass, 'lg:w-44')}>
            <option value="">All</option>
            {(Object.keys(STATE) as State[]).map((state) => (<option key={state} value={state}>{STATE[state].label}</option>))}
          </select>
        </div>

        <div className={cn('overflow-x-auto', isFetching && data && 'opacity-60')}>
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className={thClass}>Contract</th>
                <th scope="col" className={thClass}>Customer</th>
                <th scope="col" className={thClass}>Period</th>
                <th scope="col" className={cn(thClass, 'text-right')}>Products</th>
                <th scope="col" className={thClass}>State</th>
                <th scope="col" className={cn(thClass, 'text-right')}>Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {isError ? (
                <tr><td colSpan={6} className="px-4 py-10 text-center text-sm text-gray-700">{getApiErrorMessage(error, 'The contracts could not be loaded.')}</td></tr>
              ) : isLoading ? (
                <tr><td colSpan={6} className="px-4 py-10 text-center text-sm text-gray-500">Loading…</td></tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-12 text-center">
                    <p className="text-sm font-medium text-gray-900">{hasFilters ? 'No contract matches' : 'No rate contracts yet'}</p>
                    <p className="mt-1 text-sm text-gray-500">Draft a contract when rates are agreed with a customer for a period.</p>
                  </td>
                </tr>
              ) : (
                rows.map((row) => (
                  <tr key={row.id} className="hover:bg-gray-50">
                    <td className="whitespace-nowrap px-4 py-3">
                      <button type="button" onClick={() => setViewingId(row.id)} className="text-sm font-medium text-indigo-600 hover:text-indigo-500">{row.contractNumber}</button>
                      <p className="mt-0.5 text-xs text-gray-500">{row.customerReference ?? '—'}</p>
                    </td>
                    <td className="max-w-xs truncate px-4 py-3 text-sm text-gray-900">{row.customerName}</td>
                    <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-700">
                      {formatDate(row.validFrom)} to {formatDate(row.validTo)}
                      {row.state === 'in_force' && <span className={cn('block text-xs', row.daysLeft <= 30 ? 'font-medium text-amber-700' : 'text-gray-500')}>{formatNumber(row.daysLeft)} day(s) left</span>}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-right text-sm text-gray-700 tabular-nums">{formatNumber(row.items)}</td>
                    <td className="whitespace-nowrap px-4 py-3"><span className={cn(badgeClass, STATE[row.state].className)}>{STATE[row.state].label}</span></td>
                    <td className="whitespace-nowrap px-4 py-3 text-right">
                      <button type="button" onClick={() => setViewingId(row.id)} className="rounded-md px-2.5 py-1 text-xs font-semibold text-indigo-600 ring-1 ring-inset ring-indigo-200 hover:bg-indigo-50" aria-label={`Open ${row.contractNumber}`}>
                        Open
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="flex flex-col gap-3 border-t border-gray-200 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-gray-600">
            <span>Showing {firstRow}–{lastRow} of {formatNumber(total)}</span>
            <label className="flex items-center gap-2">
              Rows per page
              <select value={limit} onChange={(event) => { setLimit(Number(event.target.value)); setPage(1); }} className="rounded-md border border-gray-300 bg-white py-1 pl-2 pr-7 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500">
                {PAGE_SIZES.map((size) => (<option key={size} value={size}>{size}</option>))}
              </select>
            </label>
          </div>
          {totalPages > 1 && <Pagination currentPage={page} totalPages={totalPages} onPageChange={setPage} />}
        </div>
      </div>

      <Modal isOpen={formOpen || !!viewingId} onClose={() => (formOpen ? closeForm() : setViewingId(null))} title={formOpen ? (editing ? `Edit ${editing.contractNumber}` : 'New Rate Contract') : viewing?.contractNumber ?? 'Rate Contract'} size="xl">
        {formOpen ? (
          <ContractForm key={editing?.id ?? 'new'} contract={editing} isPending={save.isPending} onCancel={closeForm} onSubmit={(input) => save.mutate(input)} />
        ) : (
          viewingId && (
            <ContractPanel
              key={viewingId}
              contractId={viewingId}
              canEdit={canEdit}
              canApprove={canApprove}
              onEdit={(contract) => {
                setEditing(contract);
                setFormOpen(true);
              }}
              onClose={() => setViewingId(null)}
              onChanged={refreshLists}
            />
          )
        )}
      </Modal>
    </div>
  );
};

export default RateContracts;
